import React from "react";

import DecisionAttentionCard from "./DecisionAttentionCard";
import DecisionAttentionEmptyState from "./DecisionAttentionEmptyState";

export const ATTENTION_GROUPS = Object.freeze({
  CRITICAL: "Critical Attention",
  REVIEW: "Review Required",
  MONITORING: "Monitoring",
});

// This is a presentation mapping of explicit governance fields, not a score.
export function classifyDecisionAttention(matter) {
  const appetite = String(
    matter?.governanceContext?.riskAppetite ??
    matter?.governanceContext?.riskAppetitePosition ??
    matter?.riskImpact?.riskAppetite ??
    matter?.riskImpact?.riskAppetitePosition ??
    ""
  ).toUpperCase();
  const status = String(matter?.status ?? matter?.governanceStatus ?? "").toUpperCase();

  if (["OUTSIDE_TOLERANCE", "OUTSIDE RISK TOLERANCE", "OUTSIDE_RISK_APPETITE"].includes(appetite)) {
    return ATTENTION_GROUPS.CRITICAL;
  }
  if (["DRAFT", "PROPOSED", "READY_FOR_DELIBERATION", "IN_DELIBERATION"].includes(status)) {
    return ATTENTION_GROUPS.REVIEW;
  }
  return ATTENTION_GROUPS.MONITORING;
}

export default function DecisionAttentionSummary({ decisionMatters = [] }) {
  const matters = Array.isArray(decisionMatters) ? decisionMatters : [];
  const grouped = Object.values(ATTENTION_GROUPS).reduce((result, group) => ({
    ...result,
    [group]: matters.filter((matter) => classifyDecisionAttention(matter) === group),
  }), {});
  return (
    <section aria-labelledby="decision-attention-heading" className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Governance Summary</p>
      <h2 id="decision-attention-heading" className="mt-2 text-xl font-semibold text-slate-950">Decision Attention</h2>
      <p className="mt-2 text-sm text-slate-600">
        The explicit governance signals that explain why each matter requires management attention. This is not a risk score or ranking.
      </p>

      <div className="mt-6 space-y-6">
        {!matters.length ? <DecisionAttentionEmptyState /> : Object.values(ATTENTION_GROUPS).map((group) => (
          <section key={group} aria-labelledby={`attention-${group.replaceAll(" ", "-").toLowerCase()}`}>
            <h3 id={`attention-${group.replaceAll(" ", "-").toLowerCase()}`} className="text-sm font-semibold text-slate-900">
              {group} <span className="text-slate-500">({grouped[group].length})</span>
            </h3>
            {grouped[group].length > 0 && (
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                {grouped[group].map((matter, index) => (
                  <DecisionAttentionCard key={matter?.id || `${group}-${index}`} matter={matter} />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </section>
  );
}
